import { createAction, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '@/redux/rootReducer'
import patientModel, { CreatePatientDto, Patient, PatientState, UpdatePatientDto } from '@/core/models/patient.model'
import { SearchPatientsFilterParams } from '@/core/types/request'
import { patientApi } from '@/core/api/patient'

export type PatientReducerState = PatientState

export const patientAdapter = createEntityAdapter({
  selectId: (patient: Patient) => {
    patient._id = patient._id || patient.pluginId!
    return patient._id
  },
})

const patientsSelector = patientAdapter.getSelectors((state: RootState) => state.patient.patients)

export const selectPatients = patientsSelector.selectAll
export const selectPatientById = patientsSelector.selectById
export const selectNewlyCreatedPatientId = (state: RootState) => state.patient.newlyCreatedItemId
export const selectDownloadedConsentByPatientId = (state: RootState, patientId: string) => state.patient.consents[patientId]
export const selectPatientSearchParams = (state: RootState) => state.patient.search.params
export const selectPatientSearchResult = (state: RootState) => state.patient.search.result
export const selectCarteVitaleInfos = (state: RootState) => state.patient.carteVitaleInfos

export const selectPatientGetRequestTracker = (state: RootState) => state.patient.requestTracker.get
export const selectPatientListRequestTracker = (state: RootState) => state.patient.requestTracker.list
export const selectPatientCreateRequestTracker = (state: RootState) => state.patient.requestTracker.create
export const selectPatientUpdateRequestTracker = (state: RootState) => state.patient.requestTracker.update
export const selectPatientDeleteRequestTracker = (state: RootState) => state.patient.requestTracker.delete
export const selectPatientDownloadConsentRequestTracker = (state: RootState) => state.patient.requestTracker.downloadConsent
export const selectGetOrCreateOrdoclicPatientRequestTracker = (state: RootState) =>
  state.patient.requestTracker.getOrCreateOrdoclicPatient ?? { error: null, loading: false }

const initialState: PatientReducerState = {
  ...patientModel.getInitialState(),
}

const patientSlice = createSlice({
  name: patientModel.reducerName,
  initialState,
  reducers: {
    getCarteVitaleInfos(state) {
      state.requestTracker.get.loading = true
    },
    getCarteVitaleInfosSuccess(state, action: PayloadAction<Array<Record<string, string>>>) {
      state.carteVitaleInfos = action.payload
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getCarteVitaleInfosFailure(state, action) {
      state.requestTracker.get.error = action.payload
      state.requestTracker.get.loading = false
    },
    createPatient(state, action: PayloadAction<CreatePatientDto>) {
      state.requestTracker.create.loading = true
    },
    createPatientSuccess(state, action: PayloadAction<Patient>) {
      patientAdapter.addOne(state.patients, action.payload)
      state.newlyCreatedItemId = action.payload._id
      state.requestTracker.create.error = null
      state.requestTracker.create.loading = false
    },
    createPatientFailure(state, action) {
      state.newlyCreatedItemId = undefined
      state.requestTracker.create.error = action.payload
      state.requestTracker.create.loading = false
    },
    createPatientAccount(state, payload: PayloadAction<string>) {
      state.requestTracker.update.loading = true
    },
    createPatientAccountSuccess(state, action: PayloadAction<Patient>) {
      patientSlice.caseReducers.updatePatientSuccess(state, action)
    },
    createPatientAccountFailure(state, action) {
      patientSlice.caseReducers.updatePatientFailure(state, action)
    },
    getPatient(state, action: PayloadAction<string>) {
      state.requestTracker.get.loading = true
    },
    getPatientSuccess(state, action: PayloadAction<Patient>) {
      patientAdapter.upsertOne(state.patients, action.payload)
      state.requestTracker.get.error = null
      state.requestTracker.get.loading = false
    },
    getPatientFailure(state, action) {
      state.requestTracker.get.loading = false
      state.requestTracker.get.error = action.payload
    },
    getPatients: {
      reducer: (state, action: PayloadAction<SearchPatientsFilterParams>) => {
        if (state.newlyCreatedItemId) state.newlyCreatedItemId = undefined
        state.search.params = action.payload
        state.requestTracker.list.loading = true
      },
      prepare: (params: SearchPatientsFilterParams) => ({
        payload: params,
      }),
    },
    getPatientsSuccess(state, action: PayloadAction<{ items: Patient[]; totalCount: number }>) {
      const { items, totalCount } = action.payload
      patientAdapter.setAll(state.patients, items)
      state.search.result.totalCount = totalCount
      state.requestTracker.list.error = null
      state.requestTracker.list.loading = false
    },
    getPatientsFailure(state, action) {
      state.requestTracker.list.loading = false
      state.requestTracker.list.error = action.payload
    },
    updatePatient: {
      reducer: (state, action: PayloadAction<{ filter: string; update: UpdatePatientDto }>) => {
        state.requestTracker.update.loading = true
      },
      prepare: (patientId: string, updatePatientDto: UpdatePatientDto) => ({
        payload: {
          filter: patientId,
          update: updatePatientDto,
        },
      }),
    },
    updatePatientSuccess(state, action: PayloadAction<Patient>) {
      const patient = action.payload
      patientAdapter.updateOne(state.patients, { id: patient._id, changes: patient })
      state.requestTracker.update.error = null
      state.requestTracker.update.loading = false
    },
    updatePatientFailure(state, action) {
      state.requestTracker.update.error = action.payload
      state.requestTracker.update.loading = false
    },
    deletePatient(state, action: PayloadAction<string>) {
      state.requestTracker.delete.loading = true
    },
    deletePatientSuccess(state, action: PayloadAction<string>) {
      patientAdapter.removeOne(state.patients, action.payload)
      state.requestTracker.delete.error = null
      state.requestTracker.delete.loading = false
    },
    deletePatientFailure(state, action) {
      state.requestTracker.delete.error = action.payload
      state.requestTracker.delete.loading = false
    },
    downloadConsent(state, action: PayloadAction<string>) {
      const patientId = action.payload
      state.consents[patientId] = undefined
      state.requestTracker.downloadConsent.loading = true
    },
    downloadConsentSuccess(state, action: PayloadAction<{ patientId: string; consentDataUrl: string }>) {
      const { patientId, consentDataUrl } = action.payload
      state.consents[patientId] = consentDataUrl
      state.requestTracker.downloadConsent.error = null
      state.requestTracker.downloadConsent.loading = false
    },
    downloadConsentFailure(state, action) {
      state.requestTracker.downloadConsent.error = action.payload
      state.requestTracker.downloadConsent.loading = false
    },
    resetCarteVitaleInfos(state) {
      state.carteVitaleInfos = []
    },
    clearNewlyCreatedPatientId(state) {
      state.newlyCreatedItemId = undefined
    },
    getOrCreateOrdoclicPatient(state, action: PayloadAction<string>) {
      state.requestTracker.getOrCreateOrdoclicPatient = { error: null, loading: true }
    },
    getOrCreateOrdoclicPatientSuccess(state, action: PayloadAction<Patient>) {
      const patient = action.payload
      patientAdapter.upsertOne(state.patients, patient)
      state.requestTracker.getOrCreateOrdoclicPatient.error = null
      state.requestTracker.getOrCreateOrdoclicPatient.loading = false
    },
    getOrCreateOrdoclicPatientFailure(state, action) {
      state.requestTracker.getOrCreateOrdoclicPatient.error = action.payload
      state.requestTracker.getOrCreateOrdoclicPatient.loading = false
    },
  },
  extraReducers: (builder) => {
    builder
      // for backward compatibility with get patients
      .addMatcher(patientApi.endpoints.listPatients.matchFulfilled, (state, action) => {
        const { items, totalCount } = action.payload
        patientAdapter.setMany(state.patients, items)
        state.search.result.totalCount = totalCount
      })
      .addMatcher(patientApi.endpoints.searchPatients.matchFulfilled, (state, action) => {
        const { items, totalCount } = action.payload
        patientAdapter.setMany(state.patients, items)
        state.search.result.totalCount = totalCount
      })
      // for backward compatibility with get patient
      .addMatcher(patientApi.endpoints.getPatient.matchFulfilled, (state, action) => {
        patientAdapter.upsertOne(state.patients, action.payload)
      })
      .addMatcher(patientApi.endpoints.updatePatient.matchFulfilled, (state, action) => {
        const patient = action.payload
        patientAdapter.updateOne(state.patients, { id: patient._id, changes: patient })
      })
  },
})

export const patientUpdatedSocketEvent = createAction<Patient>('patient/patientUpdatedSocketEvent')

export const {
  getCarteVitaleInfos,
  getCarteVitaleInfosSuccess,
  getCarteVitaleInfosFailure,
  createPatient,
  createPatientSuccess,
  createPatientFailure,
  createPatientAccount,
  createPatientAccountSuccess,
  createPatientAccountFailure,
  getPatient,
  getPatientSuccess,
  getPatientFailure,
  getPatients,
  getPatientsSuccess,
  getPatientsFailure,
  updatePatient,
  updatePatientSuccess,
  updatePatientFailure,
  deletePatient,
  deletePatientSuccess,
  deletePatientFailure,
  downloadConsent,
  downloadConsentSuccess,
  downloadConsentFailure,
  resetCarteVitaleInfos,
  clearNewlyCreatedPatientId,
  getOrCreateOrdoclicPatient,
  getOrCreateOrdoclicPatientSuccess,
  getOrCreateOrdoclicPatientFailure,
} = patientSlice.actions

export default patientSlice.reducer
